import { getGrpcPort } from './grpcRuntime';

type LogLevel = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR';

interface LogEntry {
  timestamp: string;
  level: string;
  source: string;
  message: string;
}

const BUFFER_SIZE = 20;
const FLUSH_INTERVAL = 3000;
const DEFAULT_INFO_CAPTURE_SOURCES = new Set([
  'webLogger',
  'validation-preflight',
  'validation-performance',
  'validation.page-load',
  'validationGrpcClient',
  'directGrpcWeb',
  'operationProgressClient',
  'familyClient',
  'parameterSetup',
  'quickFillRevitService',
  'quickFillAttention',
  'mappingFlow',
  'configurationStore',
  'pcfSettingsClient',
  'pcfSettingsApply',
  'export-orchestrator',
  'setupRevision',
]);
const DEFAULT_CONSOLE_INFO_SOURCES = new Set(['export-orchestrator']);
let buffer: LogEntry[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;
const isTest = process.env.NODE_ENV === 'test';

function isVerboseWebLoggingEnabled(): boolean {
  if (isTest || typeof window === 'undefined') {
    return false;
  }

  if ((window as any).__PIPEEX_WEB_LOG_VERBOSE === true) {
    return true;
  }

  try {
    return window.localStorage?.getItem('pipeex:webLogVerbose') === '1';
  } catch {
    return false;
  }
}

function shouldCapture(level: LogLevel, source: string): boolean {
  return (
    isVerboseWebLoggingEnabled() ||
    level === 'WARN' ||
    level === 'ERROR' ||
    (level === 'INFO' && DEFAULT_INFO_CAPTURE_SOURCES.has(source))
  );
}

function shouldWriteConsole(level: LogLevel, source: string): boolean {
  return isVerboseWebLoggingEnabled()
    || level === 'WARN'
    || level === 'ERROR'
    || (level === 'INFO' && DEFAULT_CONSOLE_INFO_SOURCES.has(source));
}

function now(): string {
  return new Date().toISOString().replace('T', ' ').replace('Z', '');
}

function getBaseUrl(): string | null {
  const port = getGrpcPort();
  if (!port) return null;
  return `http://127.0.0.1:${port}`;
}

async function flush(): Promise<void> {
  if (isTest) return;
  if (buffer.length === 0) return;
  const entries = buffer.splice(0);
  const baseUrl = getBaseUrl();
  if (!baseUrl) {
    return;
  }

  try {
    const url = `${baseUrl}/api/log`;
    const resp = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(entries),
    });
    if (!resp.ok) {
      if (isVerboseWebLoggingEnabled()) {
        console.warn(`[webLogger] POST ${url} returned ${resp.status}`);
      }
    }
  } catch (err) {
    if (isVerboseWebLoggingEnabled()) {
      console.warn('[webLogger] flush failed:', err);
    }
  }
}

export function flushWebLogger(): Promise<void> {
  return flush();
}

function push(level: LogLevel, source: string, message: string): void {
  if (!shouldCapture(level, source)) {
    return;
  }

  const entry: LogEntry = { timestamp: now(), level, source, message };
  buffer.push(entry);
  if (buffer.length >= BUFFER_SIZE) {
    flush();
  }
}

/** Start periodic flush. Call once at app startup. */
export function startWebLogger(): void {
  if (isTest) return;
  if (flushTimer) return;
  flushTimer = setInterval(flush, FLUSH_INTERVAL);
  installWebErrorDiagnostics();
  push('INFO', 'webLogger', 'Web logger started [build=buffered-grpc-fix-v2], grpcPort=' + getGrpcPort());
  void flush();
}

const PROGRESS_DIAG_ENABLE_KEY = 'pipeex:progressDiag';

// Sustained diag bursts (one burst per progress-poll cycle) used to POST every
// entry on its own keepalive request. Combined with the server closing each
// browser connection, that churned ~15 sockets/sec and Chromium eventually
// failed requests with ERR_INSUFFICIENT_RESOURCES, starving real gRPC calls.
// Allow one immediate POST per interval (freeze diagnostics stay near-realtime
// after quiet periods) and batch the rest into a single follow-up request.
const DIAG_FLUSH_INTERVAL_MS = 1_000;
let diagBuffer: LogEntry[] = [];
let diagFlushTimer: ReturnType<typeof setTimeout> | null = null;
let lastDiagFlushAt = 0;

function isProgressDiagEnabled(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return window.localStorage?.getItem(PROGRESS_DIAG_ENABLE_KEY) === '1';
  } catch {
    return false;
  }
}

function postDiagEntries(entries: LogEntry[]): void {
  if (entries.length === 0) {
    return;
  }

  const baseUrl = getBaseUrl();
  if (!baseUrl) {
    diagBuffer = [];
    return;
  }

  lastDiagFlushAt = Date.now();
  try {
    void fetch(`${baseUrl}/api/log`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(entries),
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    // diagnostics must never break the app
  }
}

function flushDiagBuffer(): void {
  if (diagFlushTimer) {
    clearTimeout(diagFlushTimer);
    diagFlushTimer = null;
  }

  postDiagEntries(diagBuffer.splice(0));
}

/**
 * Diagnostic channel for the progress-freeze investigation. The batched webLog
 * buffer is lost when the page's event loop wedges, so these events bypass it
 * and land in the plugin's webui session log via keepalive POSTs. Posts are
 * rate-limited (see DIAG_FLUSH_INTERVAL_MS) so a busy poll loop cannot exhaust
 * the browser's loopback socket pool. Enable temporarily with
 * localStorage 'pipeex:progressDiag' = '1'.
 */
export function diagLog(source: string, message: string): void {
  if (!isProgressDiagEnabled()) {
    return;
  }

  if (!getBaseUrl()) {
    return;
  }

  diagBuffer.push({ timestamp: now(), level: 'INFO', source, message });

  const sinceLastFlush = Date.now() - lastDiagFlushAt;
  if (sinceLastFlush >= DIAG_FLUSH_INTERVAL_MS) {
    flushDiagBuffer();
    return;
  }

  if (!diagFlushTimer) {
    diagFlushTimer = setTimeout(flushDiagBuffer, DIAG_FLUSH_INTERVAL_MS - sinceLastFlush);
  }
}

let errorDiagnosticsInstalled = false;

// Reports escalating synchronous work before a freeze: a runaway loop shows up
// as growing longtask durations right before the log goes silent.
function installLongTaskObserver(): void {
  if (typeof PerformanceObserver === 'undefined') {
    return;
  }

  try {
    let worstSinceLastReport = 0;
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        worstSinceLastReport = Math.max(worstSinceLastReport, Math.round(entry.duration));
      }
    });
    observer.observe({ entryTypes: ['longtask'] });
    window.setInterval(() => {
      if (worstSinceLastReport > 0) {
        diagLog('progress-watchdog', `longtask worstMs=${worstSinceLastReport}`);
        worstSinceLastReport = 0;
      }
    }, 1_000);
  } catch {
    // longtask unsupported — skip silently
  }
}

/** Logs the events that precede a page death: JS errors, rejections, visibility. */
export function installWebErrorDiagnostics(): void {
  if (
    isTest ||
    typeof window === 'undefined' ||
    errorDiagnosticsInstalled ||
    !isProgressDiagEnabled()
  ) {
    return;
  }

  errorDiagnosticsInstalled = true;
  window.addEventListener('error', (event) => {
    diagLog('progress-watchdog', `window.onerror: ${event.message} at ${event.filename}:${event.lineno}`);
  });
  window.addEventListener('unhandledrejection', (event) => {
    diagLog('progress-watchdog', `unhandledrejection: ${String(event.reason)}`);
  });
  document.addEventListener('visibilitychange', () => {
    diagLog('progress-watchdog', `visibilitychange: ${document.visibilityState}`);
  });
  installLongTaskObserver();
  diagLog('progress-watchdog', 'error diagnostics installed');
}

/** Flush remaining entries (call on unload). */
export function stopWebLogger(): void {
  if (isTest) return;
  if (flushTimer) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
  flush();
}

// ==================== Public API ====================

export const webLog = {
  debug: (source: string, msg: string) => { if (isTest) return; push('DEBUG', source, msg); },
  info:  (source: string, msg: string) => {
    if (isTest) return;
    push('INFO', source, msg);
    if (shouldWriteConsole('INFO', source)) {
      console.info(`[${source}]`, msg);
    }
  },
  warn:  (source: string, msg: string) => {
    if (isTest) return;
    push('WARN', source, msg);
    if (shouldWriteConsole('WARN', source)) {
      console.warn(`[${source}]`, msg);
    }
  },
  error: (source: string, msg: string) => {
    if (isTest) return;
    push('ERROR', source, msg);
    if (shouldWriteConsole('ERROR', source)) {
      console.error(`[${source}]`, msg);
    }
  },
};
